import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { useBriefSearch, useMarketBrief } from "@/hooks/useNews";
import NewsRelevanceDisplay from "@/components/NewsRelevanceDisplay";
import EditorialDiagnosticsDisplay from "@/components/EditorialDiagnosticsDisplay";
import { classifyNewsRelevance } from "@shared/newsRelevance";
import type { NewsRange } from "@/lib/types";
import { formatDate } from "@/lib/utils";
import { ArrowRight, FileText, Newspaper, Sparkles } from "lucide-react";
import React, { useState } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";

const BriefPage: React.FC = () => {
  const { data: brief, isLoading, error } = useMarketBrief();
  const topicSearch = useBriefSearch();
  const [query, setQuery] = useState("");
  const [range, setRange] = useState<NewsRange>("30d");
  const items = brief?.items ?? [];
  const leadItem = items[0];
  const pageTitle = leadItem
    ? `Tylee Market Brief — ${leadItem.displayHeadline}`
    : "Tylee Market Brief — What Matters Today";
  const pageDescription = leadItem
    ? `${leadItem.displayHeadline}. ${leadItem.summary} Text-based relevance does not confirm a market reaction.`
    : "A plain-English brief of news that may be relevant to markets or provide financial and economic context.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://tyl.ee/brief" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://tyl.ee/brief" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <AppHeader brandText="Brief" />

        <main className="brief-main">
          <section className="brief-hero" aria-labelledby="brief-title">
            <div>
              <p className="home-kicker">INTERPRETATION LAYER · MARKET BRIEF</p>
              <h1 id="brief-title">What matters today, in plain English</h1>
              <p className="brief-hero-copy">
                News is assessed from article text for potential relevance and economic context. This assessment does not confirm a market reaction. Source headlines stay available for review.
              </p>
            </div>
            <div className="brief-hero-meta">
              <div>
                <span>Items</span>
                <strong>{items.length || "--"}</strong>
              </div>
              <div>
                <span>Cache</span>
                <strong>{brief?.diagnostics?.cache.read === "hit"
                  ? `${brief.diagnostics.cache.source} cache hit`
                  : brief?.diagnostics ? "Generated / cache status below" : "Status unavailable"}</strong>
              </div>
              <div>
                <span>Updated</span>
                <strong>{brief ? formatDate(brief.generatedAt) : "--"}</strong>
              </div>
              <div>
                <span>Engine</span>
                <strong>
                  {brief?.diagnostics?.resolvedProvider
                    ? `${brief.diagnostics.resolvedProvider} · ${brief.diagnostics.resolvedModel ?? "model ID not supplied"}`
                    : brief?.diagnostics?.outcome === "heuristic"
                      ? "Heuristic"
                      : brief?.diagnostics ? "No model result" : "Status unavailable"}
                </strong>
              </div>
            </div>
          </section>

          <EditorialDiagnosticsDisplay diagnostics={brief?.diagnostics} />

          <form className="brief-search" onSubmit={(event) => { event.preventDefault(); if (query.trim().length >= 2) topicSearch.mutate({ query: query.trim(), range }); }}>
            <label>Search current news and NYT <input value={query} onChange={(event) => setQuery(event.target.value)} maxLength={120} /></label>
            <select value={range} onChange={(event) => setRange(event.target.value as NewsRange)}><option value="24h">24h</option><option value="7d">7d</option><option value="30d">30d</option><option value="1y">1y</option></select>
            <button type="submit" disabled={topicSearch.isPending}>BUILD TOPIC BRIEF</button>
            {topicSearch.data && <button type="button" onClick={() => topicSearch.reset()}>RETURN TO TODAY'S BRIEF</button>}
          </form>

          {topicSearch.data && (
            <section className="brief-list" aria-label="Topic brief results">
              {topicSearch.data.items.map(({ item, explanation }: any) => (
                <article key={item.sourceRef} className="brief-card">
                  <div className="brief-card-head"><span className="brief-provider">{item.provider}</span><span className="brief-confidence">Text-based relevance</span></div>
                  <h2>{item.title}</h2>
                  {item.origin.startsWith("nyt-") && <p className="nyt-attribution">Data provided by The New York Times</p>}
                  <p className="brief-summary">{explanation.summary}</p>
                  <NewsRelevanceDisplay relevance={explanation.relevance ?? classifyNewsRelevance({ title: item.title, description: item.description })} />
                  <div className="brief-gridline"><div><span>Financial or economic context</span><strong>{explanation.whyItMatters}</strong></div><div><span>Source basis</span><strong>{explanation.sourceBasis}</strong></div></div>
                  <footer className="brief-footer"><a href={item.link} target="_blank" rel="noopener noreferrer" className="brief-source-link">Read full article at {item.provider}</a></footer>
                </article>
              ))}
            </section>
          )}

          {error && (
            <section className="brief-empty brief-empty--error">
              <Sparkles size={16} aria-hidden="true" />
              <span>{error instanceof Error ? error.message : "Failed to load market brief"}</span>
            </section>
          )}

          {!topicSearch.data && <section className="brief-list" aria-label="Top market brief stories">
            {isLoading && (
              <div className="brief-empty">Loading the brief...</div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="brief-empty">No brief items available yet.</div>
            )}

            {items.map((item, index) => (
              <article key={item.id} className="brief-card">
                <div className="brief-card-head">
                  <div>
                    <span className="brief-rank">{index + 1}</span>
                    <span className="brief-provider">{item.provider}</span>
                  </div>
                  <span className="brief-confidence">Text-based relevance</span>
                </div>

                <h2>{item.displayHeadline}</h2>
                <p className="brief-source">{item.sourceHeadline}</p>
                <p className="brief-summary">{item.summary}</p>
                <NewsRelevanceDisplay relevance={item.relevance ?? classifyNewsRelevance({ title: item.sourceHeadline })} newsEvidence={item.newsEvidence} />

                <div className="brief-gridline">
                  <div>
                    <span>Financial or economic context</span>
                    <strong>{item.whyItMatters}</strong>
                  </div>
                  <div>
                    <span>Topic</span>
                    <strong>{item.marketAngle}</strong>
                  </div>
                  <div>
                    <span>Region</span>
                    <strong>{item.region}</strong>
                  </div>
                </div>

                <footer className="brief-footer">
                  <time dateTime={item.publishedAt}>{formatDate(item.publishedAt)}</time>
                  {item.sourceUrl ? (
                    <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="brief-source-link">
                      <FileText size={14} aria-hidden="true" />
                      <span>Source</span>
                    </a>
                  ) : (
                    <Link href="/news" className="brief-source-link">
                      <FileText size={14} aria-hidden="true" />
                      <span>Source</span>
                    </Link>
                  )}
                </footer>
              </article>
            ))}
          </section>}

          <section className="brief-actions" aria-label="Related pages">
            <Link href="/news">
              <Newspaper size={15} aria-hidden="true" />
              <span>Open the raw terminal</span>
            </Link>
            <Link href="/monitor">
              <ArrowRight size={15} aria-hidden="true" />
              <span>See market signals</span>
            </Link>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BriefPage;
